import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Form, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import ItemSelect from "../components/ItemSelect"
import { Access, AccessController, Door } from "../data/models.interface"
import { useCreateDoorQuery } from "../hooks/queries/useCreateDoorQuery"
import { useGetAccessControllersQuery } from "../hooks/queries/useGetAccessControllersQuery"
import { useGetDoorsQuery } from "../hooks/queries/useGetDoorsQuery"
import { useUpdateDoorQuery } from "../hooks/queries/useUpdateDoorQuery"
import { Parameters } from "../hooks/useLazyQuery"

interface IDoorPage {}
const DoorPage: FunctionComponent<IDoorPage> = () => {
	const params = useParams()
	const doorId = params.doorId
	//
	const doorsQuery = useGetDoorsQuery()
	const doorLoaded = doorsQuery.data?.doors?.find((s) => s.id === doorId)
	const createDoorQuery = useCreateDoorQuery()
	const updateDoorQuery = useUpdateDoorQuery(doorId || "error")
	const saveQuery = doorId === "new" ? createDoorQuery : updateDoorQuery
	const accessControllersQuery = useGetAccessControllersQuery()
	//
	const [door, setDoor] = useState<Door>()
	const [touched, setTouched] = useState(new Map<string, boolean>())
	const [validationErrors, setValidationErrors] = useState(new Map<string, string>())
	//
	useEffect(() => {
		// only init state once
		if (door == null) {
			if (doorId === "new") setDoor(doorTemplate)
			else if (doorLoaded) setDoor(doorLoaded)
		}
	}, [doorId, doorLoaded, door])
	// error message
	const invalidIdError = doorId !== "new" && doorsQuery.data != null && doorLoaded == null ? "No Door found" : undefined
	const error = createDoorQuery.error || updateDoorQuery.error || doorsQuery.error || invalidIdError
	// loading layout
	const loading = !accessControllersQuery.data || !door
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	return (
		<Container>
			<h2 className="text-center mt-4">Door</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<DoorForm
				door={door}
				setDoor={setDoor}
				touched={touched}
				setTouched={setTouched}
				validationErrors={validationErrors}
				setValidationErrors={setValidationErrors}
				accessControllers={accessControllersQuery.data?.accessControllers!}
			/>
			<Row>
				<Col xs="auto">
					<BackButton />
				</Col>
				<Col></Col>
				<Col xs="auto">
					<SaveButton
						door={door}
						setTouched={setTouched}
						validationErrors={validationErrors}
						query={saveQuery.query}
						loading={saveQuery.loading}
					/>
				</Col>
			</Row>
		</Container>
	)
}
export default DoorPage

const LoadingLayout = ({ error }: { error?: string }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Door</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

interface IDoorForm {
	door: Door
	setDoor: (door: Door) => void
	touched: Map<string, boolean>
	setTouched: (value: Map<string, boolean>) => void
	validationErrors: Map<string, string>
	setValidationErrors: (e: Map<string, string>) => void
	accessControllers: AccessController[]
}
const DoorForm = ({
	door,
	setDoor,
	touched,
	setTouched,
	validationErrors,
	setValidationErrors,
	accessControllers,
}: IDoorForm) => {
	const setFieldTouched = (field: string) => {
		// need to make it a new object to trigger the state change
		touched = new Map(touched)
		touched.set(field, true)
		setTouched(touched)
	}
	const setDoorNumber = (value: string) => {
		const doorNumber = parseInt(value)
		setDoor({ ...door, doorNumber: doorNumber })
		//
		validationErrors = new Map(validationErrors)
		if (isNaN(doorNumber) || doorNumber < 1 || doorNumber > 4)
			validationErrors.set("doorNumber", "Door Number must be a number from 1-4.")
		else validationErrors.delete("doorNumber")
		setValidationErrors(validationErrors)
	}
	const setAccessController = (controller?: AccessController) =>
		setDoor({
			...door,
			accessControllerId: controller?.id!,
			accessControllerSerialNumber: controller?.serialNumber!,
			propertyId: controller?.propertyId!,
			propertyName: controller?.propertyName!,
			propertyShortName: controller?.propertyShortName!,
		})
	const setName = (value: string) => {
		setDoor({ ...door, name: value })
		//
		validationErrors = new Map(validationErrors)
		if (!value) validationErrors.set("name", "Name is required.")
		else validationErrors.delete("name")
		setValidationErrors(validationErrors)
	}
	const setAccess = (value?: Access) => {
		setDoor({ ...door, access: value! })
		//
		validationErrors = new Map(validationErrors)
		if (!value) validationErrors.set("access", "Access is required.")
		else validationErrors.delete("access")
		setValidationErrors(validationErrors)
	}
	return (
		<>
			<Row className="mt-4">
				<Col xs={12} sm={6} lg>
					<Form.Group className="mb-3" controlId="name">
						<Form.Label>Name</Form.Label>
						<Form.Control
							type="text"
							required
							value={door.name || ""}
							onChange={(e) => setName(e.target.value)}
							onBlur={() => setFieldTouched("name")}
							isInvalid={touched.get("name") && validationErrors.has("name")}
						/>
						<Form.Control.Feedback type="invalid">{validationErrors.get("name")}</Form.Control.Feedback>
					</Form.Group>
				</Col>
				<Col xs={12} sm={6} lg={4}>
					<Form.Group className="mb-3" controlId="accessController">
						<Form.Label>Access Controller</Form.Label>
						<ItemSelect
							items={accessControllers}
							convert={(c) => ({ id: c.id, name: c.serialNumber })}
							valueId={door.accessControllerId}
							onChange={setAccessController}
						/>
					</Form.Group>
				</Col>
				<Col xs={6} sm={6} lg>
					<Form.Group className="mb-3" controlId="doorNumber">
						<Form.Label>Door Number</Form.Label>
						<Form.Control
							type="number"
							required
							value={door.doorNumber || ""}
							onChange={(e) => setDoorNumber(e.target.value)}
							onBlur={() => setFieldTouched("doorNumber")}
							isInvalid={touched.get("doorNumber") && validationErrors.has("doorNumber")}
						/>
						<Form.Control.Feedback type="invalid">{validationErrors.get("doorNumber")}</Form.Control.Feedback>
					</Form.Group>
				</Col>
				<Col xs={12} sm={6} lg={4}>
					<Form.Group className="mb-3" controlId="access">
						<Form.Label>Access</Form.Label>
						<Form.Select required value={door.access} onChange={(e) => setAccess(e.target.value as Access)}>
							<option value={Access.LOCKED}>{Access.LOCKED}</option>
							<option value={Access.MANAGED}>{Access.MANAGED}</option>
							<option value={Access.UNLOCKED}>{Access.UNLOCKED}</option>
						</Form.Select>
					</Form.Group>
				</Col>
			</Row>
		</>
	)
}

const BackButton = () => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-4" onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}

interface ISaveButton {
	door: Door
	setTouched: (value: Map<string, boolean>) => void
	validationErrors: Map<string, string>
	query: (door: Parameters<Door>) => Promise<void>
	loading: boolean
}
const SaveButton = ({ door, setTouched, validationErrors, query, loading }: ISaveButton) => {
	const navigate = useNavigate()
	const save = async () => {
		// show errors on all fields
		const touchMap = new Map<string, boolean>()
		touchMap.set("name", true)
		touchMap.set("accessControllerId", true)
		touchMap.set("doorNumber", true)
		touchMap.set("access", true)
		setTouched(touchMap)
		//
		try {
			const errorCount = Array.from(validationErrors).filter((e) => e?.length > 0).length
			if (errorCount === 0) {
				await query({ body: door })
				navigate(-1)
			}
		} catch (e) {
			return
		}
	}
	return (
		<Button variant="primary" className="mb-4" disabled={loading} onClick={save}>
			Save
		</Button>
	)
}

const doorTemplate = {
	access: "MANAGED",
} as Door
